import styled from "styled-components";
import { memo, useEffect, useState } from "react";

import { BreadcrumbsCommon } from "@components";
import { breakpoint, theme } from "@utilities";
import { IBreadcrumbs } from "@interfaces";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { PATH_SHARE_QUOTES_WITH_CONTACT, PATH_SHARE_QUOTES_WITH_EMAIL, PATH_QUOTES } from "@routes";
import { selectAccount, useAppDispatch, useAppSelector, useIsLoggedIn } from "@redux";
import { loadAllQuotes, loadQuotes } from "src/Redux/Slices/allQuotes";
import { loadMyQuotes } from "src/Redux/Slices/myQuotes";
import { quoteApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

interface IPoint {
    active: boolean;
}

interface IProcessOrderHeaderProps {
    stepLabels: string[];
    activeStepIndex: number;
    breadcrumbs: IBreadcrumbs[];
    changeStep: (newStepIndex: number) => void;
}

export const ProcessOrderHeader = memo((props: IProcessOrderHeaderProps) => {
    const { breadcrumbs, stepLabels, activeStepIndex, changeStep } = props;
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [showInvitationsPopup, setShowInvitationsPopup] = useState(false);
    const [sentInvitations, setSentInvitations] = useState<any[]>([]);
    const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
    const [resendingEmail, setResendingEmail] = useState<string | null>(null);
    const [newShareEmail, setNewShareEmail] = useState("");
    const [isSharingNew, setIsSharingNew] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { quoteId: quoteIdFromParam } = useParams<{ quoteId: string }>();
    const quoteId = quoteIdFromParam!;
    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";
    const { metadata } = useAppSelector(selectAccount);
    const isLoggedIn = useIsLoggedIn();
    const quotes = useAppSelector((state) => state.allQuotes.quotes);
    const allQuotes = useAppSelector((state) => state.allQuotes.allQuotes);
    const myQuotes = useAppSelector((state) => state.myQuotes.quotes);
    const isOrderTabEnabled = Boolean(metadata?.is_order_tab_enabled);

    const numberOfShares = isOrderTabEnabled
        ? (
              allQuotes?.find((quote) => quote.id === parseInt(quoteId)) ||
              myQuotes?.find((quote) => quote.id === parseInt(quoteId))
          )?.number_of_shares
        : quotes?.find((quote) => quote.id === parseInt(quoteId))?.number_of_shares;

    const handleShareWithContacts = () => {
        navigate(`${PATH_SHARE_QUOTES_WITH_CONTACT}?quoteId=${quoteId}`);
    };

    const handleShareViaEmail = () => {
        navigate(`${PATH_SHARE_QUOTES_WITH_EMAIL}?quoteId=${quoteId}`);
    };

    const handleOpenInvitations = async () => {
        setShowInvitationsPopup(true);
        setIsLoadingInvitations(true);
        setSentInvitations([]);
        try {
            const response = await quoteApi.getSentInvitations(quoteId);
            const data = (response.data.data || []).filter(
                (inv: any) => inv && typeof inv.email === "string" && inv.email.trim()
            );
            setSentInvitations(data);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to load shared invitations";
            ErrorToast({ Message: errorMessage });
            setSentInvitations([]);
        } finally {
            setIsLoadingInvitations(false);
        }
    };

    const handleResendInvitation = async (email: string) => {
        setResendingEmail(email);
        try {
            await quoteApi.ShareQuotesViaEmail({ quoteId, shareToEmail: email });
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setResendingEmail(null);
        }
    };

    const handleShareNewFromPopup = async () => {
        const trimmed = newShareEmail.trim();
        if (!trimmed) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
            ErrorToast({ Message: "Please enter a valid email address" });
            return;
        }
        setIsSharingNew(true);
        try {
            await quoteApi.ShareQuotesViaEmail({ quoteId, shareToEmail: trimmed });
            setSentInvitations((prev) => [...prev, { email: trimmed }]);
            setNewShareEmail("");
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setIsSharingNew(false);
        }
    };

    useEffect(() => {
        // Only load quotes if user is logged in
        if (isLoggedIn) {
            if (isOrderTabEnabled) {
                dispatch(loadAllQuotes());
                dispatch(loadMyQuotes());
            } else {
                dispatch(loadQuotes());
            }
        }
    }, [dispatch, isOrderTabEnabled, isLoggedIn]);

    return (
        <StyledContainer>
            <div className="main">
                <div className="breadcrumbs">
                    <BreadcrumbsCommon data={breadcrumbs} />
                    {!isPending && location.pathname.startsWith(PATH_QUOTES) && (
                        <>
                            <ActionsContainer>
                                {!metadata?.is_default_agent && (
                                    <ShareButton onClick={() => setShowSharePopup(true)}>
                                        <img
                                            src="/images/shareBlue.svg"
                                            alt="share"
                                            width={18}
                                            height={18}
                                        />
                                    </ShareButton>
                                )}
                                {showSharePopup && (
                                    <ShareModalOverlay onClick={() => setShowSharePopup(false)}>
                                        <ShareModalContent onClick={(e) => e.stopPropagation()}>
                                            <ShareModalHeader>
                                                <h3>Share with your friends and family</h3>
                                                <ShareCloseButton
                                                    onClick={() => setShowSharePopup(false)}
                                                >
                                                    <div
                                                        style={{
                                                            backgroundColor:
                                                                theme.colors.items_background,
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            padding: "5px",
                                                            borderRadius: theme.borderRadius.small,
                                                        }}
                                                    >
                                                        <img
                                                            src="/images/close_icon.svg"
                                                            alt="Close"
                                                            width={18}
                                                            height={18}
                                                        />
                                                    </div>
                                                </ShareCloseButton>
                                            </ShareModalHeader>
                                            <div>
                                                <p>Choose Sharing Method</p>
                                                <ShareMethodsContainer>
                                                    <ShareMethodButton
                                                        onClick={handleShareWithContacts}
                                                    >
                                                        <div>
                                                            <div
                                                                style={{
                                                                    backgroundColor:
                                                                        theme.colors.items_background,
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    padding: "5px",
                                                                    borderRadius:
                                                                        theme.borderRadius.small,
                                                                }}
                                                            >
                                                                <img
                                                                    src="/images/userBlue.svg"
                                                                    alt="Share"
                                                                    width={18}
                                                                    height={18}
                                                                />
                                                            </div>
                                                            Share with Contacts
                                                        </div>
                                                        <p>Share from your addresss book</p>
                                                    </ShareMethodButton>
                                                    <ShareMethodButton onClick={handleShareViaEmail}>
                                                        <div>
                                                            <img
                                                                src="/images/mailBlue.svg"
                                                                alt="Share"
                                                            />
                                                            Share via Email
                                                        </div>
                                                        <p>Send invitation to specific Email address</p>
                                                    </ShareMethodButton>
                                                </ShareMethodsContainer>
                                            </div>
                                        </ShareModalContent>
                                    </ShareModalOverlay>
                                )}
                                <ShareCount onClick={handleOpenInvitations}>
                                    <img
                                        src="/images/userBlue.svg"
                                        alt="Share count"
                                        width={18}
                                        height={18}
                                    />
                                    <span>{numberOfShares}</span>
                                </ShareCount>
                            </ActionsContainer>
                        </>
                    )}
                </div>
                {stepLabels.map((stepLabel, stepLabelIndex) => {
                    const isActiveOrBefore = stepLabelIndex <= activeStepIndex;

                    return (
                        <StyledPoint key={stepLabelIndex} active={isActiveOrBefore}>
                            <button
                                className="number"
                                disabled={stepLabelIndex === activeStepIndex}
                                style={{
                                    cursor:
                                        stepLabelIndex === activeStepIndex
                                            ? "auto"
                                            : stepLabelIndex > activeStepIndex
                                              ? "not-allowed"
                                              : "pointer",
                                }}
                                onClick={() => {
                                    if (stepLabelIndex < activeStepIndex) {
                                        changeStep(stepLabelIndex);
                                    }
                                }}
                            >
                                {stepLabelIndex + 1}
                                <span className="title">{stepLabel}</span>
                            </button>
                            {stepLabelIndex !== stepLabels.length - 1 && <div className="line" />}
                        </StyledPoint>
                    );
                })}
            </div>
            {showInvitationsPopup && (
                <InvitationsOverlay onClick={() => setShowInvitationsPopup(false)}>
                    <InvitationsContent onClick={(e) => e.stopPropagation()}>
                        <InvitationsHeader>
                            <h3>Shared With</h3>
                            <ShareCloseButton onClick={() => setShowInvitationsPopup(false)}>
                                <img
                                    src="/images/close_icon.svg"
                                    alt="Close"
                                    width={24}
                                    height={24}
                                />
                            </ShareCloseButton>
                        </InvitationsHeader>
                        <InvitationsList>
                            {isLoadingInvitations ? (
                                <p style={{ color: "#6e7375", fontSize: "14px" }}>Loading...</p>
                            ) : sentInvitations.length === 0 ? (
                                <p style={{ color: "#6e7375", fontSize: "14px" }}>No invitations sent yet</p>
                            ) : (
                                sentInvitations
                                    .filter((inv, idx, self) => idx === self.findIndex((i) => i.email === inv.email))
                                    .map((inv) => (
                                        <InvitationItem key={inv.email}>
                                            <span className="email">{inv.email}</span>
                                            {!metadata?.is_default_agent && (
                                                <InvitationResendBtn
                                                    onClick={() => handleResendInvitation(inv.email)}
                                                    disabled={resendingEmail === inv.email}
                                                >
                                                    {resendingEmail === inv.email ? "Sending..." : "Resend"}
                                                </InvitationResendBtn>
                                            )}
                                        </InvitationItem>
                                    ))
                            )}
                        </InvitationsList>
                        {!metadata?.is_default_agent && (
                            <InvitationShareRow>
                                <InvitationEmailInput
                                    type="email"
                                    placeholder="Enter email to share"
                                    value={newShareEmail}
                                    onChange={(e) => setNewShareEmail(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleShareNewFromPopup(); }}
                                    disabled={isSharingNew}
                                />
                                <InvitationShareBtn
                                    onClick={handleShareNewFromPopup}
                                    disabled={isSharingNew || !newShareEmail.trim()}
                                >
                                    {isSharingNew ? "Sharing..." : "Share"}
                                </InvitationShareBtn>
                            </InvitationShareRow>
                        )}
                    </InvitationsContent>
                </InvitationsOverlay>
            )}
        </StyledContainer>
    );
});

const StyledContainer = styled.div`
    ${breakpoint.breakTablet`
        background-color: #F2F2F2;
    `}

    .main {
        display: flex;
        justify-content: center;
        margin: 50px auto;
        position: relative;

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
            margin: 90px auto;
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}

        .breadcrumbs {
            position: absolute;
            left: 0;
            right: 0;
            top: -40px;
            display: flex;
            align-items: center;
            justify-content: space-between;

            ${breakpoint.breakOnlyMobile`
                left: 16px;
                right: 16px;
            `}
        }
    }
`;

const ActionsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ShareButton = styled.button`
    margin-top: 4px;
    background: none;
    border: none;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ShareCount = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: ${theme.colors.button_text1};
    cursor: pointer;
    background: none;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    -webkit-appearance: none;
    appearance: none;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }

    span {
        text-decoration: underline;
    }
`;

const StyledPoint = styled.div<IPoint>`
    display: flex;
    align-items: center;

    .number {
        cursor: pointer;
        position: relative;
        width: 24px;
        height: 24px;
        text-align: center;
        background-color: ${(props) =>
            props.active ? props.theme.colors.main : props.theme.colors.gray_4};
        border-radius: 50%;
        color: ${(props) => (props.active ? props.theme.colors.button_text : "#fff")};
        font-weight: 700;
        font-size: 14px;
        line-height: 24px;
        margin: 0 4px;
    }

    .line {
        width: 60px;
        border-bottom: 1px dashed #bdbdbd;

        ${breakpoint.breakTablet`
            width: 133px;
        `}
    }

    .title {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        color: ${(props) =>
            props.active ? props.theme.colors.button_text1 : props.theme.colors.gray_4};
        font-weight: 500;
        font-size: 14px;
        white-space: nowrap;
        line-height: 22px;
        margin-top: 5px;
    }
`;

const ShareModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const ShareModalContent = styled.div`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 570px;

    p {
        font-family: "Plus Jakarta Sans", sans-serif;
        font-size: 18px;
        font-weight: 600;
    }
`;

const ShareModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 {
        font-size: 28px;
        font-weight: 600;
        width: calc(100% - 40px);

        ${breakpoint.breakOnlyMobile`
            font-size: 20px;    
        `}
    }
    margin-bottom: 20px;
`;

const ShareCloseButton = styled.button`
    cursor: pointer;
`;

const ShareMethodsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
`;

const ShareMethodButton = styled.button`
    display: flex;
    flex-direction: column;
    align-items: start;
    padding: 12px 16px;
    border: 1px solid #c1c4c6;
    border-radius: ${theme.borderRadius.medium};
    background: white;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #000000;
    -webkit-appearance: none;
    appearance: none;

    div {
        display: flex;
        gap: 10px;
        font-size: 18px;
        color: #000000;
    }
    p {
        padding-left: 34px;
        font-size: 14px;
        font-weight: 400;
        color: #6e7375;
    }
`;

const InvitationsOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const InvitationsContent = styled.div`
    background-color: white;
    padding: 24px;
    border-radius: 12px;
    width: 450px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;

    ${breakpoint.breakOnlyMobile`
        width: 100%;
        border-radius: 12px 12px 0 0;
        max-height: 70vh;
    `}
`;

const InvitationsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const InvitationsList = styled.div`
    flex: 1;
    overflow-y: auto;
    margin-bottom: 16px;
    min-height: 60px;
`;

const InvitationItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }

    .email {
        font-size: 14px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        margin-right: 8px;
    }
`;

const InvitationResendBtn = styled.button`
    background: none;
    border: 1px solid ${theme.colors.button_text1};
    color: ${theme.colors.button_text1};
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${theme.colors.button_text1};
        color: white;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const InvitationShareRow = styled.div`
    display: flex;
    gap: 8px;
    border-top: 1px solid #e0e0e0;
    padding-top: 12px;
`;

const InvitationEmailInput = styled.input`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #c1c4c6;
    border-radius: 6px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: ${theme.colors.button_text1};
    }

    &:disabled {
        opacity: 0.5;
    }
`;

const InvitationShareBtn = styled.button`
    padding: 8px 16px;
    background: ${theme.colors.button_text1};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
